// src/components/SavedViews.tsx
//
// Saved views ("bookmarks") — a compact control that lets a user save the
// current tab's filter state under a name and re-apply it later. Backed by
// the agent's /api/views endpoints (per-page JSON blobs).
//
// Contract with the host tab:
//   - `page`       — stable identifier, becomes the /api/views ?page= key
//   - `getState`   — returns the tab's current control state (plain JSON)
//   - `applyState` — receives a previously saved state; the tab sets its
//                    controls from whatever keys it recognizes (saved states
//                    from older builds may miss keys — tolerate that)
//
// Older agents don't have /api/views — when the initial GET fails the whole
// control hides itself silently rather than rendering a broken dropdown.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, BookmarkPlus, ChevronDown, RefreshCw, Trash2 } from 'lucide-react';
import { apiV2 as api } from '../services/apiV2';
import type { SavedView } from '../types';
import Modal from './Modal';

interface SavedViewsProps {
  page: string;
  getState: () => Record<string, unknown>;
  applyState: (s: Record<string, unknown>) => void;
}

export function SavedViews({ page, getState, applyState }: SavedViewsProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);

  const q = useQuery({
    queryKey: ['views', page],
    queryFn: () => api.getSavedViews(page),
    staleTime: 60_000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const saveM = useMutation({
    mutationFn: (body: { page: string; name: string; state: string }) => api.putSavedView(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['views', page] }),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.deleteSavedView(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['views', page] }),
  });

  // Close the dropdown on any outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Older agent without /api/views → hide the control entirely.
  if (q.isError) return null;

  const views = q.data ?? [];

  function applyView(v: SavedView) {
    try {
      const parsed = JSON.parse(v.state) as Record<string, unknown>;
      applyState(parsed);
    } catch {
      // Corrupt state blob — nothing sensible to apply.
    }
    setOpen(false);
  }

  function startSave() {
    setName('');
    setNaming(true);
    setOpen(false);
  }

  function confirmSave() {
    const trimmed = name.trim();
    if (!trimmed) return;
    saveM.mutate(
      { page, name: trimmed, state: JSON.stringify(getState()) },
      { onSuccess: () => setNaming(false) },
    );
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        title="Saved views"
        className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1"
      >
        <Bookmark size={11} /> Views
        <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 z-30 w-60 rounded-lg border border-slate-700/60 bg-slate-900 shadow-xl shadow-black/40 py-1">
          {q.isLoading ? (
            <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-1.5">
              <RefreshCw size={11} className="animate-spin" /> Loading…
            </div>
          ) : views.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">No saved views yet</div>
          ) : (
            views.map((v) => (
              <div key={v.id} className="group flex items-center gap-1 px-1">
                <button
                  onClick={() => applyView(v)}
                  className="flex-1 min-w-0 text-left px-2 py-1.5 text-xs text-slate-300 hover:text-white hover:bg-slate-800/70 rounded-md truncate"
                  title={v.name}
                >
                  {v.name}
                </button>
                <button
                  onClick={() => deleteM.mutate(v.id)}
                  disabled={deleteM.isPending}
                  title="Delete view"
                  className="p-1.5 rounded-md text-slate-600 hover:text-red-400 hover:bg-slate-800/70 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-40"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))
          )}
          <div className="border-t border-slate-800 mt-1 pt-1">
            <button
              onClick={startSave}
              className="w-full text-left px-3 py-1.5 text-xs text-blue-300 hover:text-blue-200 hover:bg-slate-800/70 flex items-center gap-1.5"
            >
              <BookmarkPlus size={12} /> Save view…
            </button>
          </div>
        </div>
      )}

      {naming && (
        <Modal title="Save view" onClose={() => setNaming(false)}>
          <div className="space-y-4">
            <p className="text-xs text-slate-400">
              Saves the tab's current filters under a name so you can re-apply them later.
            </p>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmSave(); }}
              placeholder="View name"
              className="w-full px-3 py-2 rounded-md bg-slate-800/60 border border-slate-700/50 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
            />
            {saveM.isError && (
              <div className="text-xs text-red-300">{(saveM.error as Error).message}</div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setNaming(false)}
                className="px-3 py-1.5 text-xs rounded-md border border-slate-700/50 text-slate-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={confirmSave}
                disabled={!name.trim() || saveM.isPending}
                className="px-3 py-1.5 text-xs rounded-md bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40 flex items-center gap-1.5"
              >
                {saveM.isPending && <RefreshCw size={11} className="animate-spin" />} Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
